/**
 * Admin Music panel — Feb 2026.
 *
 * Lets the admin upload MP3 (or M4A/AAC/OGG/WAV) songs that drive
 * the persistent mini-player at the top of every public page.
 * Songs are stored in MongoDB GridFS via `/api/playlist/upload`.
 */
import { useCallback, useEffect, useState } from "react";
import { useMusicPlayer } from "../context/MusicPlayerContext";

const MAX_BYTES = 30 * 1024 * 1024; // 30 MB

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AdminMusicPanel({ adminAxios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [reordering, setReordering] = useState(false);
  const { refreshPlaylist } = useMusicPlayer();

  // Upload form state
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!adminAxios) return;
    setLoading(true);
    setError("");
    try {
      const res = await adminAxios.get("/playlist");
      setItems(res.data?.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load songs.");
    } finally {
      setLoading(false);
    }
  }, [adminAxios]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!adminAxios) return;
    setError("");
    setInfo("");
    if (!title.trim()) { setError("Title is required."); return; }
    if (!file) { setError("Pick an audio file."); return; }
    if (file.size > MAX_BYTES) { setError("File must be 30 MB or smaller."); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("artist", artist.trim());
      await adminAxios.post("/playlist/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo("Song uploaded.");
      setTitle("");
      setArtist("");
      setFile(null);
      const fi = document.getElementById("music-file-input");
      if (fi) fi.value = "";
      await load();
      await refreshPlaylist();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "Upload failed.";
      if (typeof detail === "string" && detail) msg = detail;
      else if (status === 413) msg = "File exceeds the 30 MB cap.";
      else if (status) msg = `Upload failed (HTTP ${status}).`;
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!adminAxios) return;
    if (!window.confirm("Delete this song from the playlist?")) return;
    setError("");
    setInfo("");
    try {
      await adminAxios.delete(`/playlist/${id}`);
      setInfo("Deleted.");
      await load();
      await refreshPlaylist();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Delete failed.");
    }
  };

  // Persist the current `items` order to the backend (writes
  // sort_order 0..N-1 to each song). Used by the Up/Down arrows
  // AND the Shuffle button — both mutate `items` first, then call
  // this. We pass the desired order in explicitly to avoid a stale-
  // closure read of the state setter.
  const persistOrder = useCallback(async (orderedItems) => {
    if (!adminAxios) return;
    setReordering(true);
    setError("");
    setInfo("");
    try {
      const ids = orderedItems.map((it) => it.id);
      await adminAxios.post("/playlist/reorder", { ids });
      await refreshPlaylist();
      setInfo("Order saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save order.");
      // Re-pull from the server so the UI matches the truth.
      await load();
    } finally {
      setReordering(false);
    }
  }, [adminAxios, refreshPlaylist, load]);

  const moveItem = (idx, delta) => {
    const next = items.slice();
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    persistOrder(next);
  };

  const shuffleItems = () => {
    if (items.length < 2) return;
    // Fisher–Yates so every permutation is equally likely. We seed
    // from Math.random — the admin is the only consumer so we don't
    // need cryptographic randomness here.
    const next = items.slice();
    for (let i = next.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [next[i], next[j]] = [next[j], next[i]];
    }
    setItems(next);
    persistOrder(next);
  };

  return (
    <div className="rm-admin-library" data-testid="admin-music-panel">
      <form
        className="rm-admin-library-form"
        onSubmit={onUpload}
        data-testid="music-upload-form"
      >
        <h3
          className="font-serif-display italic text-2xl text-[#FFE7A0] mb-4"
          data-testid="music-upload-title"
        >
          Add a song
        </h3>
        <p className="rm-admin-library-helper">
          MP3 / M4A / AAC / OGG / WAV. Max 30 MB per song. New songs
          appear immediately in the player at the top of every page.
        </p>
        <label className="rm-admin-library-field">
          <span className="rm-admin-library-label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. Holy Holy Holy"
            className="rm-admin-library-input"
            data-testid="music-title-input"
            required
          />
        </label>
        <label className="rm-admin-library-field">
          <span className="rm-admin-library-label">Artist (optional)</span>
          <input
            type="text"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            maxLength={200}
            placeholder="e.g. Audrey Assad"
            className="rm-admin-library-input"
            data-testid="music-artist-input"
          />
        </label>
        <label className="rm-admin-library-field">
          <span className="rm-admin-library-label">Audio file</span>
          <input
            id="music-file-input"
            type="file"
            accept="audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,.mp3,.m4a,.aac,.ogg,.wav"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="rm-admin-library-file"
            data-testid="music-file-input"
            required
          />
        </label>
        <div className="rm-admin-library-form-actions">
          <button
            type="submit"
            className="rm-btn-solid"
            disabled={uploading}
            data-testid="music-submit"
          >
            {uploading ? "Uploading…" : "Add song"}
          </button>
        </div>
      </form>

      {error && (
        <div className="rm-cm-alert rm-cm-alert-error mt-4" data-testid="music-error">
          {error}
        </div>
      )}
      {info && (
        <div className="rm-cm-alert rm-cm-alert-info mt-4" data-testid="music-info">
          {info}
        </div>
      )}

      <h3
        className="font-serif-display italic text-xl text-[#FFE7A0] mt-10 mb-3"
        data-testid="music-current-title"
      >
        Current playlist
      </h3>
      {loading && (
        <div className="rm-cm-empty" data-testid="music-loading">Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="rm-cm-empty" data-testid="music-empty">
          No songs uploaded yet.
        </div>
      )}
      {!loading && items.length > 0 && (
        <ul className="rm-admin-rows" data-testid="music-rows">
          {items.map((it, idx) => (
            <li
              key={it.id}
              className="rm-admin-row"
              data-testid={`music-row-${it.id}`}
            >
              <div className="rm-admin-row-body-wrap">
                <div className="rm-admin-row-meta">
                  <span className="rm-cm-name">{it.title}</span>
                  {it.artist && (
                    <span className="rm-cm-time">{it.artist}</span>
                  )}
                  <span className="rm-cm-time">{formatTime(it.uploaded_at)}</span>
                  <span className="rm-admin-badge">
                    {formatBytes(it.size_bytes)}
                  </span>
                </div>
                <div className="rm-admin-row-actions">
                  <button
                    type="button"
                    className="rm-cm-link rm-cm-link-muted"
                    onClick={() => moveItem(idx, -1)}
                    disabled={reordering || idx === 0}
                    data-testid={`music-move-up-${it.id}`}
                    title="Move up"
                    aria-label={`Move ${it.title} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="rm-cm-link rm-cm-link-muted"
                    onClick={() => moveItem(idx, 1)}
                    disabled={reordering || idx === items.length - 1}
                    data-testid={`music-move-down-${it.id}`}
                    title="Move down"
                    aria-label={`Move ${it.title} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="rm-cm-link rm-cm-link-muted rm-admin-danger"
                    onClick={() => onDelete(it.id)}
                    data-testid={`music-delete-${it.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
